"use client";

import { Button } from "antd";
import { useRouter } from "next/navigation";
import { useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { getClientAuth } from "@/lib/firebase-client";
import { trackClientError } from "@/lib/datadog";
import { loginPathWithNext, sanitizeNextPath } from "@/lib/redirect";

export default function LoginButton({ next }: { next?: string | null }) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  async function handleSignIn() {
    setLoading(true);
    try {
      const auth = getClientAuth();
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");
      const cred = await signInWithPopup(auth, provider);
      const idToken = await cred.user.getIdToken();
      const googleIdToken =
        GoogleAuthProvider.credentialFromResult(cred)?.idToken ?? null;

      const res = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ idToken, googleIdToken }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        await signOut(auth);
        const code = body.error === "AccessDenied" ? "AccessDenied" : "OAuthCallbackError";
        router.replace(loginPathWithNext(next, { error: code }));
        return;
      }

      router.replace(sanitizeNextPath(next) ?? "/");
      router.refresh();
    } catch (err) {
      // The Firebase error code separates "the user closed the popup" from a
      // genuinely broken sign-in, which otherwise look identical here.
      void trackClientError(err, {
        source: "login.sign_in",
        code: (err as { code?: string })?.code,
      });
      router.replace(loginPathWithNext(next, { error: "OAuthCallbackError" }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      type="primary"
      block
      size="large"
      loading={loading}
      onClick={handleSignIn}
    >
      Sign in with Google
    </Button>
  );
}
